// VideoStyle.js
import React, { useEffect, useState, useRef } from 'react'
import { Card } from 'antd'
import CinematicIcon from '../../../../assets/video-cinematic.png'
import AnimeIcon from '../../../../assets/video-anime.png'
import ComicBookIcon from '../../../../assets/video-comic.png'
import OrigamiIcon from '../../../../assets/video-origami.png'
import DisneyIcon from '../../../../assets/video-disney.png'
import RealisticIcon from '../../../../assets/video-realistic.png'

import './VideoStyle.scss'
import StepTitle from '../StepTitle'

const { Meta } = Card

const videoStyles = [
    {
    key: 'realistic',
    title: 'Realistic',
    description: 'Photorealistic style with natural lighting and textures',
    icon: RealisticIcon
  },
  {
    key: 'cinematic black & white',
    title: 'Cinematic',
    description: 'Photorealistic style with natural lighting and textures',
    icon: CinematicIcon
  },
  {
    key: 'comic book',
    title: 'Comic Book',
    description: 'Bold colors, strong outlines, and dramatic shading',
    icon: ComicBookIcon
  },
  {
    key: 'anime',
    title: 'Anime',
    description: 'Japanese animation style with vibrant colors',
    icon: AnimeIcon
  },
  {
    key: 'origami',
    title: 'Origami',
    description: 'Futuristic cyberpunk aesthetic with neon elements',
    icon: OrigamiIcon
  },
  {
    key: 'disney cartoon',
    title: 'Cartoon',
    description: 'Classic Disney characters with vibrant colors',
    icon: DisneyIcon
  }
]

// Lazy Image Component with fallback for older browsers
const LazyImage = ({ src, alt, className }) => {
  const [isLoaded, setIsLoaded] = useState(false)
  const [isInView, setIsInView] = useState(false)
  const imgRef = useRef()

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true)
          observer.disconnect()
        }
      },
      { threshold: 0.1 }
    )

    if (imgRef.current) {
      observer.observe(imgRef.current)
    }

    return () => observer.disconnect()
  }, [])

  const handleLoad = () => {
    setIsLoaded(true)
  }

  return (
    <div ref={imgRef} className={className}>
      {isInView && (
        <img
          src={src}
          alt={alt}
          loading="lazy"
          onLoad={handleLoad}
          style={{
            opacity: isLoaded ? 1 : 0,
            transition: 'opacity 0.3s ease',
            width: '100%',
            height: '100%',
            objectFit: 'contain'
          }}
        />
      )}
      {!isLoaded && isInView && (
        <div 
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            height: '100%',
            backgroundColor: '#f5f5f5',
            color: '#ccc'
          }}
        >
          Loading...
        </div>
      )}
    </div>
  )
}

const VideoStyle = ({ setFormData, formData, onValidationChange }) => {
  const [selectedStyle, setSelectedStyle] = useState(null)

  useEffect(() => {
    // If formData has a videoStyle, set it as the selected style
    if (formData && formData.videoStyle) {
      setSelectedStyle(formData.videoStyle.key)
    }
  }, [formData])

  const handleStyleSelect = (key) => {
    setSelectedStyle(key)
    // Update formData with the new video style selection
    const selectedStyleObj = videoStyles.find(style => style.key === key)
    setFormData(prev => ({
      ...prev,
      videoStyle: selectedStyleObj
    }))
    onValidationChange(prev => ({ ...prev, videoStyle: true }))
  }

  return (
    <div className="video-style-selector">
      <StepTitle
        iconType="camera"
        title="Video Style"
        description="Choose the style for your video."
      />
      <div className="shot-list-cards">
        {videoStyles.map(style => (
          <Card
            key={style.key}
            className={`shot-list-card ${
              selectedStyle === style.key ? 'selected' : ''
            }`}
            onClick={() => handleStyleSelect(style.key)}
            cover={
              <div
                className="shot-list-card-image"
                style={{ backgroundColor: '#f0f0f0' }}
              >
                <LazyImage 
                  src={style.icon} 
                  alt={style.title}
                />
              </div>
            }
          >
            <Meta title={style.title} description={style.description} />
          </Card>
        ))}
      </div>
    </div>
  )
}

export default VideoStyle